import React, { useState, useEffect } from "react";
import { Save, Globe, FileText, Info, MessageSquare, Instagram, Facebook, Twitter, Youtube, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminSiteSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: "TIKA",
    tagline: "Événements culturels Hip-Hop, Afrobeat & Dancehall",
    address: "",
    city: "",
    postal_code: "",
    country: "France",
    phone: "",
    email: "",
    support_email: "support@tikaculture.com",
    cgv_content: "",
    cgu_content: "",
    privacy_policy: "",
    about_title: "À propos de TIKA",
    about_content: "",
    about_image: "",
    contact_title: "Contactez-nous",
    contact_intro: "",
    contact_address_visible: true,
    footer_text: "TIKA - La billetterie des cultures urbaines",
    footer_copyright: "© 2025 TIKA. Tous droits réservés.",
    instagram_url: "",
    facebook_url: "",
    twitter_url: "",
    tiktok_url: "",
    youtube_url: "",
    hero_title: "TIKA",
    hero_subtitle: "Hip-Hop/RNB • Afrobeat • Dancehall/Caraïbes"
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("tika_admin_token");
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Paramètres enregistrés !");
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Paramètres du Site</h2>
        <Button onClick={handleSubmit} disabled={saving} size="sm">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "..." : "Sauvegarder tout"}
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid grid-cols-5 mb-4">
          <TabsTrigger value="general" className="text-xs">
            <Globe className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Général</span>
          </TabsTrigger>
          <TabsTrigger value="about" className="text-xs">
            <Info className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">À propos</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="text-xs">
            <MessageSquare className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Contact</span>
          </TabsTrigger>
          <TabsTrigger value="legal" className="text-xs">
            <FileText className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Légal</span>
          </TabsTrigger>
          <TabsTrigger value="social" className="text-xs">
            <Instagram className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Social</span>
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <form onSubmit={handleSubmit} className="glass-card rounded-lg p-4 space-y-4">
            <h3 className="font-bold mb-2">Informations générales</h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">Nom du site</Label>
                <Input id="company_name" name="company_name" value={settings.company_name} onChange={handleInputChange} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="tagline">Slogan</Label>
                <Input id="tagline" name="tagline" value={settings.tagline} onChange={handleInputChange} className="mt-1" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hero_title">Titre Hero (accueil)</Label>
                <Input id="hero_title" name="hero_title" value={settings.hero_title} onChange={handleInputChange} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="hero_subtitle">Sous-titre Hero</Label>
                <Input id="hero_subtitle" name="hero_subtitle" value={settings.hero_subtitle} onChange={handleInputChange} className="mt-1" />
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Footer</h4>
              <div>
                <Label htmlFor="footer_text">Texte du footer</Label>
                <Input id="footer_text" name="footer_text" value={settings.footer_text} onChange={handleInputChange} className="mt-1" />
              </div>
              <div className="mt-3">
                <Label htmlFor="footer_copyright">Copyright</Label>
                <Input id="footer_copyright" name="footer_copyright" value={settings.footer_copyright} onChange={handleInputChange} className="mt-1" />
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </form>
        </TabsContent>

        {/* About Page */}
        <TabsContent value="about">
          <form onSubmit={handleSubmit} className="glass-card rounded-lg p-4 space-y-4">
            <h3 className="font-bold mb-2">Page "À propos"</h3>
            
            <div>
              <Label htmlFor="about_title">Titre de la page</Label>
              <Input id="about_title" name="about_title" value={settings.about_title} onChange={handleInputChange} className="mt-1" />
            </div>

            <div>
              <Label htmlFor="about_content">Contenu</Label>
              <Textarea id="about_content" name="about_content" value={settings.about_content} onChange={handleInputChange} rows={10} className="mt-1" placeholder="Présentez votre plateforme TIKA..." />
            </div>

            <div>
              <Label htmlFor="about_image">Image (URL)</Label>
              <Input id="about_image" name="about_image" value={settings.about_image} onChange={handleInputChange} className="mt-1" placeholder="https://..." />
              {settings.about_image && (
                <img src={settings.about_image} alt="Aperçu" className="mt-2 w-32 h-20 object-cover rounded" />
              )}
            </div>

            <Button type="submit" disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </form>
        </TabsContent>

        {/* Contact Page */}
        <TabsContent value="contact">
          <form onSubmit={handleSubmit} className="glass-card rounded-lg p-4 space-y-4">
            <h3 className="font-bold mb-2">Page "Contact"</h3>
            
            <div>
              <Label htmlFor="contact_title">Titre</Label>
              <Input id="contact_title" name="contact_title" value={settings.contact_title} onChange={handleInputChange} className="mt-1" />
            </div>

            <div>
              <Label htmlFor="contact_intro">Introduction</Label>
              <Textarea id="contact_intro" name="contact_intro" value={settings.contact_intro} onChange={handleInputChange} rows={3} className="mt-1" placeholder="Une question ? Contactez-nous..." />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Coordonnées</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email public</Label>
                  <Input id="email" name="email" type="email" value={settings.email} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="support_email">Email support</Label>
                  <Input id="support_email" name="support_email" type="email" value={settings.support_email} onChange={handleInputChange} className="mt-1" />
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" name="phone" type="tel" value={settings.phone} onChange={handleInputChange} className="mt-1" />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Adresse</h4>
              <div className="flex items-center gap-2 mb-3">
                <input type="checkbox" id="contact_address_visible" name="contact_address_visible" checked={settings.contact_address_visible} onChange={handleInputChange} className="rounded" />
                <Label htmlFor="contact_address_visible" className="cursor-pointer">Afficher l'adresse sur la page contact</Label>
              </div>
              <div>
                <Label htmlFor="address">Adresse</Label>
                <Input id="address" name="address" value={settings.address} onChange={handleInputChange} className="mt-1" />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <Label htmlFor="postal_code">Code postal</Label>
                  <Input id="postal_code" name="postal_code" value={settings.postal_code} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="city">Ville</Label>
                  <Input id="city" name="city" value={settings.city} onChange={handleInputChange} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="country">Pays</Label>
                  <Input id="country" name="country" value={settings.country} onChange={handleInputChange} className="mt-1" />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </form>
        </TabsContent>

        {/* Legal Pages */}
        <TabsContent value="legal">
          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="glass-card rounded-lg p-4 space-y-4">
              <h3 className="font-bold">CGV - Conditions Générales de Vente</h3>
              <Textarea id="cgv_content" name="cgv_content" value={settings.cgv_content} onChange={handleInputChange} rows={8} className="font-mono text-sm" placeholder="Article 1 - Objet..." />
              <Button type="submit" disabled={saving} size="sm">
                <Save className="w-3 h-3 mr-1" />
                Sauvegarder CGV
              </Button>
            </form>

            <form onSubmit={handleSubmit} className="glass-card rounded-lg p-4 space-y-4">
              <h3 className="font-bold">CGU - Conditions Générales d'Utilisation</h3>
              <Textarea id="cgu_content" name="cgu_content" value={settings.cgu_content} onChange={handleInputChange} rows={8} className="font-mono text-sm" placeholder="Article 1 - Présentation..." />
              <Button type="submit" disabled={saving} size="sm">
                <Save className="w-3 h-3 mr-1" />
                Sauvegarder CGU
              </Button>
            </form>

            <form onSubmit={handleSubmit} className="glass-card rounded-lg p-4 space-y-4">
              <h3 className="font-bold">Politique de confidentialité</h3>
              <Textarea id="privacy_policy" name="privacy_policy" value={settings.privacy_policy} onChange={handleInputChange} rows={8} className="font-mono text-sm" placeholder="Vos données personnelles..." />
              <Button type="submit" disabled={saving} size="sm">
                <Save className="w-3 h-3 mr-1" />
                Sauvegarder Confidentialité
              </Button>
            </form>
          </div>
        </TabsContent>

        {/* Social Links */}
        <TabsContent value="social">
          <form onSubmit={handleSubmit} className="glass-card rounded-lg p-4 space-y-4">
            <h3 className="font-bold mb-2">Réseaux Sociaux</h3>
            <p className="text-sm text-muted-foreground mb-4">Ces liens apparaîtront dans le footer du site.</p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Instagram className="w-5 h-5 text-pink-500 flex-shrink-0" />
                <Input name="instagram_url" value={settings.instagram_url} onChange={handleInputChange} placeholder="https://instagram.com/..." />
              </div>
              <div className="flex items-center gap-3">
                <Facebook className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <Input name="facebook_url" value={settings.facebook_url} onChange={handleInputChange} placeholder="https://facebook.com/..." />
              </div>
              <div className="flex items-center gap-3">
                <Twitter className="w-5 h-5 text-sky-500 flex-shrink-0" />
                <Input name="twitter_url" value={settings.twitter_url} onChange={handleInputChange} placeholder="https://twitter.com/..." />
              </div>
              <div className="flex items-center gap-3">
                <Youtube className="w-5 h-5 text-red-500 flex-shrink-0" />
                <Input name="youtube_url" value={settings.youtube_url} onChange={handleInputChange} placeholder="https://youtube.com/..." />
              </div>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
                <Input name="tiktok_url" value={settings.tiktok_url} onChange={handleInputChange} placeholder="https://tiktok.com/@..." />
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer les liens
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};
